import * as R from 'ramda';
import {
  ORGANISATION_RECEIVE_MESSAGES,
  ORGANISATION_RECEIVE_MESSAGE,
} from '../../admin/actions';
import {
  SOCIAL_RECEIVE_FEED,
  SOCIAL_RECEIVE_SCHEDULED_MESSAGES,
  SOCIAL_RECEIVE_IMPORTANT_MESSAGES,
  SOCIAL_RECEIVE_UNREAD_MESSAGES,
  SOCIAL_POST_MESSAGE,
  SOCIAL_EDIT_MESSAGE,
  SOCIAL_RECEIVE_MESSAGE,
} from '../actions';

// Action type for chat messages with links
export const CHAT_RECEIVE_MESSAGES_WITH_LINKS = 'chat/RECEIVE_MESSAGES_WITH_LINKS';

function getRelatedLinksFromAction(action) {
  return action.related?.links || action.meta?.related?.links || [];
}

function mergeLinksIntoState(state, relatedLinks) {
  if (!relatedLinks.length) return state;

  const linksToAdd = relatedLinks.reduce((acc, link) => {
    const normalizedUrl = link.url.trim().replace(/\/$/, '');
    const existingLink = state[normalizedUrl];
    const updatedLink = existingLink ? R.mergeDeepRight(existingLink, link) : link;
    return R.assoc(normalizedUrl, updatedLink, acc);
  }, {});

  return R.merge(state, linksToAdd);
}

// Global links store - stores all link metadata by link URL for SOCIAL and CHAT messages
// Structure: { [url]: LinkMetadata }
export default (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_FEED:
    case SOCIAL_RECEIVE_SCHEDULED_MESSAGES:
    case SOCIAL_RECEIVE_IMPORTANT_MESSAGES:
    case SOCIAL_RECEIVE_UNREAD_MESSAGES:
    case ORGANISATION_RECEIVE_MESSAGES:
    case ORGANISATION_RECEIVE_MESSAGE:
    case CHAT_RECEIVE_MESSAGES_WITH_LINKS:
      return mergeLinksIntoState(state, getRelatedLinksFromAction(action));
    case SOCIAL_POST_MESSAGE:
    case SOCIAL_EDIT_MESSAGE:
      // Extract links from message meta.related.links if available
      if (action.message?.meta?.related?.links) {
        const messageLinks = action.message.meta.related.links;

        if (messageLinks.length > 0) {
          const linksToAdd = messageLinks.reduce((acc, link) => {
            // Normalize URL for consistent lookup
            const normalizedUrl = link.url.trim().replace(/\/$/, '');
            // If link already exists, merge with existing data to update it
            const existingLink = state[normalizedUrl];
            const updatedLink = existingLink ? R.mergeDeepRight(existingLink, link) : link;
            return R.assoc(normalizedUrl, updatedLink, acc);
          }, {});

          return R.merge(state, linksToAdd);
        }
      }

      return state;
    case SOCIAL_RECEIVE_MESSAGE:
      return mergeLinksIntoState(state, getRelatedLinksFromAction(action));
    default:
      return state;
  }
};

// Selector to get links for a message by matching URLs in the message text
export const getLinksForMessage = R.curry((state, messageText) => {
  if (!messageText) return [];

  const allLinks = state.social.links;

  // First, extract URLs from markdown links: [text](url)
  // This is needed because the plain URL regex would include the trailing ")"
  // from markdown syntax, preventing matches.
  const mdLinkRegex = /\[(?:[^\]]*)\]\((https?:\/\/[^)]+)\)/g;
  const mdUrls = [];
  let mdMatch;
  // eslint-disable-next-line no-cond-assign
  while ((mdMatch = mdLinkRegex.exec(messageText)) !== null) {
    mdUrls.push(mdMatch[1]);
  }

  // Strip markdown links from text to avoid double-matching with plain URL regex
  const textWithoutMdLinks = messageText.replace(/\[(?:[^\]]*)\]\(https?:\/\/[^)]+\)/g, '');

  // Match remaining plain URLs
  const urlPattern = /(https?:\/\/[^\s<>"']+|\/[^\s<>"']+)/gim;
  const plainUrls = textWithoutMdLinks.match(urlPattern) || [];

  // Combine both sources, removing duplicates
  const textUrls = [...new Set([...mdUrls, ...plainUrls])];

  // Find matching links from global store
  const matchedLinks = textUrls
    .map((url) => {
      const normalizedTextUrl = url.trim().replace(/\/$/, '');

      // Try direct match first
      let matchedLink = allLinks[normalizedTextUrl];

      if (!matchedLink) {
        // Try matching by extracting resource ID
        const linkEntries = Object.entries(allLinks);
        matchedLink = linkEntries.find(([linkUrl, link]) => {
          try {
            const linkPath = linkUrl.startsWith('http')
              ? new URL(linkUrl).pathname
              : linkUrl;
            const textPath = normalizedTextUrl.startsWith('http')
              ? new URL(normalizedTextUrl).pathname
              : normalizedTextUrl;

            // Extract IDs from paths and compare
            const linkIdMatch = linkPath.match(/\/([^/]+)$/);
            const textIdMatch = textPath.match(/\/([^/]+)$/);

            if (linkIdMatch && textIdMatch && linkIdMatch[1] === textIdMatch[1]) {
              return true;
            }
          } catch (e) {
            // URL parsing failed, skip
          }

          return false;
        })?.[1];
      }

      return matchedLink;
    })
    .filter(Boolean); // Remove undefined values

  // Remove duplicates by link id and type (support both old and new format)
  const uniqueLinks = matchedLinks.reduce((acc, link) => {
    const linkId = link.source_id || link.id;
    const linkType = link.source_type || link.type;
    const exists = acc.find((l) => {
      const lId = l.source_id || l.id;
      const lType = l.source_type || l.type;
      return lId === linkId && lType === linkType;
    });
    if (!exists) {
      acc.push(link);
    }
    return acc;
  }, []);

  return uniqueLinks;
});

